<template>
  <div id="map"
    role="region">
    <slot />
  </div>
</template>

<script>
import Mapbox from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import redPin from '@/locations-map/assets/red-pin.png'
import grapePin from '@/locations-map/assets/grape-pin.png'
import slatePin from '@/locations-map/assets/slate-pin.png'
import bluePin from '@/locations-map/assets/blue-pin.png'
import plumPin from '@/locations-map/assets/plum-pin.png'
import magentaPin from '@/locations-map/assets/magenta-pin.png'
import MapboxLanguage from '@mapbox/mapbox-gl-language'

export default {
  name: 'MapContainer',

  props: {
    mapboxConfig: {
      type: Object,
      required: true,
      default: () => {
        return {}
      },
    },
    mapContext: {
      type: Object,
      required: true,
      default: () => {
        return {}
      },
    },
    deepLinkLocation: {
      type: Object,
      required: true,
      default: () => {
        return {}
      },
    },
  },

  data() {
    return {
      centerView: [-330.02, 12.3624],
      zoomLevel: 1.5,
      pins: [
        { image: redPin, id: 'redPin' },
        { image: grapePin, id: 'grapePin' },
        { image: slatePin, id: 'slatePin' },
        { image: plumPin, id: 'plumPin' },
        { image: bluePin, id: 'bluePin' },
        { image: magentaPin, id: 'magentaPin' },
      ],
      mapboxlanguage: null,
    }
  },

  created() {
    window.addEventListener('mp_zoomToBounds', this.zoomToBounds)
    window.addEventListener('mp_zoomToLocation', this.zoomToLocation)
  },

  destroyed() {
    window.removeEventListener('mp_zoomToBounds', this.zoomToBounds)
    window.removeEventListener('mp_zoomToLocation', this.zoomToLocation)
  },

  mounted() {
    this.setupMap()
  },

  methods: {
    setupMap() {
      Mapbox.accessToken = this.mapboxConfig.mapboxToken
      const mapContext = new Mapbox.Map({
        container: 'map',
        style: this.mapboxConfig.style,
        center: this.centerView,
        zoom: this.zoomLevel,
        attributionControl: false,
      })

      this.mapboxlanguage = new MapboxLanguage({
        defaultLanguage: this.mapboxConfig.language,
      })

      mapContext.addControl(this.mapboxlanguage)
      mapContext.addControl(new Mapbox.NavigationControl({ showCompass: false }))
      mapContext.scrollZoom.disable()
      
      this.$emit('setContext', mapContext)

      mapContext.on('load', () => {
        this.loadImages()
      })
    },
    loadImages() {
      Promise.all(this.pins.map(pin => new Promise((resolve, reject) => {
        this.mapContext.loadImage(pin.image, (error, image) => {
          if (error) reject(error)
          this.mapContext.addImage(pin.id, image)
          resolve()
        })
      }))).then(() => {
        this.$emit('mapReady', true)
        if (this.deepLinkLocation.id) {
          this.zoomToLocation({
            detail: { center: [this.deepLinkLocation.longitude, this.deepLinkLocation.latitude], zoom: 13 },
          })
        }
      })
    },
    zoomToLocation(event) {
      const state = event.detail
      this.mapContext.flyTo({
        center: state.center,
        zoom: state.zoom, // 0 = Earth, 15 = Buildings, 23 = Street
        speed: 5,
        curve: 1,
        easing(t) {
          return t
        },
      })
    },
    zoomToBounds(event) {
      const state = event.detail
      this.mapContext.fitBounds(state.bounds, {
        maxZoom: state.zoom,
        padding: 100,
      })
    },
  },
}
</script>
